import dayjs from "dayjs";
import React, { HTMLAttributes, useEffect, useRef, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { twMerge } from "tailwind-merge";
import {
  createIllegalParkingReport,
  getEVInfo,
  getExistingConfirmedReports,
} from "../../../apis/parkingbeesRelatedService";
import type {
  IIllegalParkingReportResponse,
  IParking,
  IParkingBeesEvChargerVacancyResponse,
} from "../../../apis/parkingbeesRelatedService/type";
import BaseSelect from "../../../components/atoms/baseSelect";
import Header from "../../../components/molecules/header";
import { customShowToast } from "../../../helper/toast";
import useGeneralStore from "../../../store/general";

type TReportProps = HTMLAttributes<HTMLDivElement>;

const WHATSAPP_LINK = "https://wa.me/85284013062";
const MAX_IMAGE_WIDTH = 1280;
const MAX_IMAGE_HEIGHT = 1280;
const IMAGE_QUALITY = 0.8;

const formatPlateInput = (value: string) =>
  value
    .toUpperCase()
    .replaceAll("I", "")
    .replaceAll("O", "")
    .replaceAll("Q", "");

const estimateImageSizeLabel = (blob: Blob | null, imageData: string | null) => {
  if (blob) return `${Math.round(blob.size / 1024)} KB`;
  if (!imageData) return "unknown size";

  const prefixIdx = imageData.indexOf("base64,");
  const b64 = prefixIdx > -1 ? imageData.slice(prefixIdx + 7) : imageData;
  const approxBytes = Math.ceil((b64.length * 3) / 4);
  return `${Math.round(approxBytes / 1024)} KB (est)`;
};

const copyTextToClipboard = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const ta = document.createElement("textarea");
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  (document as any).execCommand?.("copy");
  document.body.removeChild(ta);
};

const Report: React.FC<TReportProps> = (props) => {
  const { ...otherProps } = props;

  const [reports, setReports] = useState<
    IIllegalParkingReportResponse[] | null | undefined
  >(undefined);
  const [isOpen, setIsOpen] = useState(false);

  const hasPageAccess = true;

  // form state
  const [plate, setPlate] = useState("");
  const [remark, setRemark] = useState("");
  const [imageData, setImageData] = useState<string | null>(null);
  // store final blob used for upload (helps show size / send as FormData)
  const imageBlobRef = useRef<Blob | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [selectedParkingId, setSelectedParkingId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // parking list from store
  const { parkingListWithEvCharger, setParkingListWithEvCharger } =
    useGeneralStore((state) => state);

  // normalize options for BaseSelect (ensure id is string)
  const parkingOptions =
    parkingListWithEvCharger?.map((p: IParking) => ({
      id: String(p.id),
      display_name: p.display_name,
    })) || [];

  const fetchAllEVInfo = async () => {
    try {
      const evInfoResponse = await getEVInfo();
      const _parkingListWithEvCharger = evInfoResponse.map(
        (item: IParkingBeesEvChargerVacancyResponse) => item.parking,
      );
      setParkingListWithEvCharger(_parkingListWithEvCharger);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchReports = async () => {
    try {
      setReports(null); // set loading state
      const reportsResponse = await getExistingConfirmedReports();
      setReports(reportsResponse);
    } catch (error) {
      console.error(error);
      setReports(undefined);
    }
  };

  useEffect(() => {
    fetchAllEVInfo();
    fetchReports();
  }, []);

  const resetForm = () => {
    setSelectedParkingId("");
    setPlate("");
    setRemark("");
    setImageData(null);
    imageBlobRef.current = null;
    if (fileRef.current) fileRef.current.value = "";
  };

  const closeModal = () => {
    setIsOpen(false);
    resetForm();
  };

  const resizeImageFile = (
    file: File,
    maxWidth = 1280,
    maxHeight = 1280,
    quality = 0.8,
  ): Promise<string> =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        try {
          let { width, height } = img;
          const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(width * ratio);
          canvas.height = Math.round(height * ratio);
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            URL.revokeObjectURL(url);
            return reject(new Error("2D context not available"));
          }
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL("image/jpeg", quality);
          URL.revokeObjectURL(url);
          resolve(dataUrl);
        } catch (err) {
          URL.revokeObjectURL(url);
          reject(err);
        }
      };
      img.onerror = (err) => {
        URL.revokeObjectURL(url);
        reject(err);
      };
      img.src = url;
    });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return;

    try {
      // keep original file as fallback blob (before resize)
      imageBlobRef.current = f;

      // resize using existing helper (returns dataURL)
      const resizedDataUrl = await resizeImageFile(
        f,
        MAX_IMAGE_WIDTH,
        MAX_IMAGE_HEIGHT,
        IMAGE_QUALITY,
      );
      setImageData(resizedDataUrl);

      // convert dataURL -> blob to get accurate final size and for FormData upload
      try {
        const blob = await (await fetch(resizedDataUrl)).blob();
        imageBlobRef.current = blob;
        console.info("resized image size (KB):", Math.round(blob.size / 1024));
      } catch (convErr) {
        console.warn(
          "failed to convert dataURL to blob, keeping original file blob",
          convErr,
        );
      }
    } catch (err) {
      // fallback: read original file as data URL and keep original file blob
      imageBlobRef.current = f;
      const reader = new FileReader();
      reader.onload = () => {
        setImageData(reader.result as string);
      };
      reader.readAsDataURL(f);
    }
  };

  const openBrowserCamera = async () => {
    // 永遠使用 file input 喚起系統相機或圖庫選擇（在大多數瀏覽器與 WebView 都能觸發）
    try {
      // customShowToast(`try to open camera`, "info");
      fileRef.current?.click();
    } catch (err) {
      customShowToast(`無法開啟相機${err}`, "error");
      console.error("openBrowserCamera click failed:", err);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    // testing block: require remark to equal "55699"
    // if (remark.trim() !== "55699") {
    //   customShowToast("功能測試中...", "error");
    //   return;
    // }

    if (!selectedParkingId.trim()) {
      customShowToast("請選擇停車場", "error");
      return;
    }
    if (!plate.trim()) {
      customShowToast("請填寫違泊車牌", "error");
      return;
    }

    if (imageData === null) {
      customShowToast("請上傳違泊照片作為證據", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      // find original parking entry by comparing string ids (parkingList has numeric ids)
      const selectedParking =
        parkingListWithEvCharger?.find(
          (parking: IParking) => String(parking.id) === selectedParkingId,
        ) ?? null;

      // ensure we send numeric parking_id
      const parkingId = selectedParking
        ? selectedParking.id
        : Number(selectedParkingId);

      const response = await createIllegalParkingReport(
        parkingId,
        plate.trim(),
        remark.trim(),
        imageBlobRef.current as File,
      );

      if (response.message === "duplicate_existing_report") {
        customShowToast("已有相同的違泊回報", "error");
        return;
      }

      if (response) {
        customShowToast("已收到您的回報!", "success");
        closeModal();
      } else {
        customShowToast("回報失敗，請稍後再試", "error");
      }

      await fetchReports();
    } catch (error: any) {
      const sizeLabel = estimateImageSizeLabel(imageBlobRef.current, imageData);
      customShowToast(
        `${error?.message ?? "發生錯誤"}（檔案大小：${sizeLabel}）`,
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div
      className="flex flex-col min-h-[100dvh] h-full text-white bg-[#0b0b0b]"
      {...otherProps}
    >
      <Header nolink />
      <main className="flex-1 px-5 laptop:px-16 py-8 bg-white text-black">
        <div className="flex flex-row justify-between text-3xl laptop:text-4xl font-extrabold mb-4">
          <span>違泊車輛</span>

          {hasPageAccess && (
            <button
              type="button"
              onClick={fetchReports}
              disabled={reports === null}
              title="刷新列表"
              className={`flex items-center gap-2 px-3 py-1 rounded-md hover:bg-mainBlue text-sm text-white bg-mainLightBlue transition ${
                reports === null ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <svg
                className={`w-4 h-4 ${reports === null ? "animate-spin" : ""}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M21 12a9 9 0 1 1-3.3-6.7"></path>
                <polyline points="21 3 21 9 15 9"></polyline>
              </svg>
              <span>重新整理</span>
            </button>
          )}
        </div>
        <p className="text-gray-400 mb-2">
          此頁面將顯示當前已被核實有違泊行為的在場車輛。
        </p>

        <div className="text-sm text-mainLightBlue mb-4">
          * 若發現違泊行為，請提交回報並上傳照片作為證據。
        </div>

        {!hasPageAccess && (
          <div className="flex rounded-lg items-center justify-center min-h-[50dvh] text-black bg-mainDisable">
            <div className="text-center">
              <h1 className="text-2xl font-bold mb-2">即將推出</h1>
              <h1 className="text-2xl font-bold mb-2">Coming Soon</h1>
            </div>
          </div>
        )}

        {/* skeleton loading */}
        {hasPageAccess && reports === null && (
          <div className="animate-pulse space-y-4">
            <div className="h-24 bg-gray-700 rounded"></div>
            <div className="h-24 bg-gray-700 rounded"></div>
            <div className="h-24 bg-gray-700 rounded"></div>
          </div>
        )}

        {/* no reports */}
        {hasPageAccess &&
          (reports === undefined || (reports && reports.length === 0)) && (
            <div className="w-full min-h-[300px] py-8 flex flex-col items-center justify-center text-center text-gray-400">
              <div className="text-2xl font-extrabold text-green-400 mb-1">
                非常棒 🎉
              </div>
              <div className="text-sm mb-3">目前沒有任何違泊車輛</div>
            </div>
          )}

        {hasPageAccess && reports && reports.length > 0 && (
          <section className="grid grid-cols-1 laptop:grid-cols-2 gap-4 max-h-[60vh] overflow-auto pr-2">
            {reports.map((r) => (
              <div
                key={r.id}
                className="relative bg-white border border-gray-200 rounded-2xl p-4 flex gap-4 items-start shadow-md hover:shadow-lg transition-shadow duration-150"
              >
                {/* image */}
                <div className="w-[140px] h-[140px] laptop:w-[180px] laptop:h-[180px] rounded-2xl bg-gray-50 flex items-center justify-center overflow-hidden border border-gray-100">
                  {r.report_image ? (
                    <img
                      src={r.report_image}
                      alt="evidence"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-gray-300 text-xs">未提供照片</div>
                  )}
                </div>

                {/* content */}
                <div className="flex flex-col flex-1 min-h-[140px] justify-between">
                  <div className="flex flex-col gap-1">
                    <div className="text-sm font-semibold text-gray-800">
                      {r?.parking?.display_name ?? "-"}
                    </div>

                    <div className="flex items-center gap-3 mt-1">
                      <div className="text-xs text-gray-500">違泊車牌</div>
                      <div className="px-3 py-1 rounded-full bg-red-50 border border-red-200 text-red-600 font-semibold text-sm">
                        {r?.car_plate ?? "-"}
                      </div>
                    </div>

                    <div className="text-sm text-gray-600 mt-3 break-words">
                      {r?.remark ? (
                        <span>回報內容：{r.remark}</span>
                      ) : (
                        <span className="text-gray-400">無補充說明</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <div className="text-xs text-gray-500">
                      {dayjs(r.createdAt).format("YYYY-MM-DD HH:mm")}
                    </div>
                    {/* small status badge */}
                    {r.illegal_parking_id ? (
                      <div className="text-xs text-red-600 font-bold px-2 py-1 rounded-md bg-red-50 border border-red-100">
                        已核實違泊
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 px-2 py-1 rounded-md bg-gray-50 border border-gray-100">
                        待確認
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* contact section */}
        {hasPageAccess && (
          <section className="mt-8">
            <h2 className="text-lg font-bold text-white mb-2">聯絡資訊</h2>
            <p className="text-sm text-gray-400 mb-1">
              如有任何問題查詢，請聯絡我們：
            </p>
            <div className="flex text-sm flex-row gap-2 items-center">
              <button
                type="button"
                onClick={async () => {
                  try {
                    await copyTextToClipboard(WHATSAPP_LINK);
                    customShowToast(
                      "已複製 Whatsapp 連結，請貼到瀏覽器開啟",
                      "success",
                    );
                  } catch (err) {
                    customShowToast(
                      `無法複製連結，請手動複製：${WHATSAPP_LINK}`,
                      "error",
                    );
                    console.error("copy wa link failed:", err);
                  }
                }}
                className="flex flex-row gap-2 text-green-600"
              >
                <FaWhatsapp size={20} className="hover:text-green-600" />
                複製 Whatsapp 連結
              </button>
              <div className="text-sm text-gray-400">
                或電郵到{" "}
                <span className="text-mainBlue ">hello@asgardev.com.hk</span>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Floating Action Button */}
      {!isOpen && hasPageAccess && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed right-6 bottom-6 z-50 w-14 h-14 rounded-full bg-mainBlue hover:bg-blue-600 flex items-center justify-center text-white text-3xl shadow-xl"
          aria-label="新增違泊回報"
        >
          +
        </button>
      )}

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center">
          {/* darker overlay + subtle blur to separate modal */}
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={closeModal}
            aria-hidden
          />
          <form
            onSubmit={handleSubmit}
            className="relative z-50 w-[92%] max-w-lg text-black bg-gradient-to-b bg-white border border-gray-700/60 rounded-2xl p-6 laptop:p-8 shadow-2xl ring-1 ring-white/5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <div className="text-xl font-bold">充電違泊回報</div>
              <button
                type="button"
                onClick={closeModal}
                className="text-black text-2xl"
                aria-label="關閉"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* 停車場選單（僅能選擇，不能手動輸入） */}
              <div>
                <label
                  htmlFor="parking-select"
                  className="block text-sm text-black mb-1"
                >
                  停車場（選填）
                </label>
                <BaseSelect
                  id="parking-select"
                  name="parkingId"
                  options={parkingOptions || []}
                  value={selectedParkingId}
                  onChange={(v) => setSelectedParkingId(v)}
                  placeholder="未指定停車場"
                />
              </div>

              <div>
                <label className="block text-sm text-black mb-1">
                  違泊車牌*
                </label>
                <input
                  value={plate}
                  onChange={(e) => {
                    const regex = /^[a-zA-Z0-9]{0,8}$/;
                    if (regex.test(e.target.value)) {
                      setPlate(formatPlateInput(e.target.value));
                    }
                  }}
                  required
                  className="w-full border bg-mainDark border-transparent rounded-lg px-3 py-2 text-white placeholder-gray-400"
                  placeholder="請輸入違泊的車牌號碼"
                />
              </div>

              <div>
                <label className="block text-sm text-black mb-1">
                  上傳證據（僅拍照）
                </label>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={openBrowserCamera}
                    className={twMerge(
                      "px-4 py-2 rounded-lg bg-mainBlue text-white text-sm",
                      isSubmitting && "opacity-50 cursor-not-allowed",
                    )}
                    disabled={isSubmitting}
                  >
                    拍照上傳
                  </button>
                  <span className="text-sm text-gray-400 self-center">
                    若裝置不支援，會開啟檔案選擇（僅照片）
                  </span>
                </div>

                {/* hidden fallback input (capture 建議值) */}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  capture
                  onChange={handleFileChange}
                  className="hidden"
                />
                {imageData && (
                  <img
                    src={imageData}
                    alt="preview"
                    className="mt-2 w-56 h-56 laptop:w-72 laptop:h-72 object-cover rounded-lg border"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm text-black mb-1">備註</label>
                <textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  rows={3}
                  maxLength={80}
                  className="w-full bg-mainDark border border-transparent rounded-lg px-3 py-2 text-white placeholder-gray-400"
                  placeholder="補充說明，如時間、位置等"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 rounded-lg bg-black border border-gray-700 text-white"
              >
                取消
              </button>
              <button
                type="submit"
                className={twMerge(
                  "px-4 py-2 rounded-lg bg-mainBlue text-white font-medium",
                  isSubmitting && "opacity-50 cursor-not-allowed",
                )}
                disabled={isSubmitting}
              >
                {isSubmitting ? "提交中..." : "提交回報"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Report;
